"use client";

import React, { useEffect, useMemo, useState } from "react";
import PropTypes from "prop-types";
import { AnimatePresence, motion, useReducedMotion } from "motion/react";
import ProductTag from "../../atoms/ProductTag";
import { formatClassNames } from "../../../utils/common";

const CYCLE_MS = 2000;
const FLIP_DURATION_S = 0.4;
const FLIP_OFFSET_PX = 8;
const CUSTOM_SLIDE_SLOT_HEIGHT_CLASS = "h-28";

/**
 * Renders product attribute tags in a fixed slot. When multiple attributes exist,
 * they cycle with a fade-and-slide transition via Motion.
 *
 * @param {Object} props
 * @param {Array} [props.productAttributes] - Resolved CMS attribute tags (or custom slides) to display
 * @param {boolean} props.isMobile - Whether the component is rendered on mobile
 * @param {Function} [props.renderItem] - Optional custom renderer for a slide. Defaults to rendering a ProductTag.
 * @returns {JSX.Element|null}
 */
const ProductTagFlipper = ({ productAttributes = [], isMobile, renderItem, isFromPDP }) => {
  const attributes = useMemo(
    () => productAttributes.filter((attr) => attr?.description || attr?.isCustomSlide),
    [productAttributes],
  );
  const attributesKey = useMemo(() => attributes.map((attr) => attr.description ?? attr.id).join("|"), [attributes]);
  const hasCustomSlide = useMemo(() => attributes.some((attr) => attr?.isCustomSlide), [attributes]);
  const slotHeightClass = hasCustomSlide ? CUSTOM_SLIDE_SLOT_HEIGHT_CLASS : "";
  const shouldReduceMotion = useReducedMotion();
  const renderSlide =
    renderItem ?? ((attr) => <ProductTag productAttributes={attr} isMobile={isMobile} isTruncate={true} />);

  const [activeIndex, setActiveIndex] = useState(0);

  useEffect(() => {
    setActiveIndex(0);
  }, [attributesKey]);

  useEffect(() => {
    if (attributes.length < 2 || shouldReduceMotion) return undefined;

    const intervalId = setInterval(() => {
      setActiveIndex((prev) => (prev + 1) % attributes.length);
    }, CYCLE_MS);

    return () => clearInterval(intervalId);
  }, [attributesKey, attributes.length, shouldReduceMotion]);

  if (attributes.length === 0) return null;

  if (attributes.length === 1) {
    return (
      <div className={`pointer-events-none w-full min-w-0 ${slotHeightClass} flex items-center`}>
        {renderSlide(attributes[0])}
      </div>
    );
  }

  const activeAttribute = attributes[activeIndex];
  const motionOffset = shouldReduceMotion ? 0 : FLIP_OFFSET_PX;

  return (
    <div className={`pointer-events-none relative w-full min-w-0 ${slotHeightClass}`} aria-live="polite">
      <AnimatePresence mode="wait" initial={false}>
        <motion.div
          key={`${attributesKey}-${activeIndex}`}
          className={formatClassNames("h-full w-full min-w-0", isFromPDP && "absolute left-0 top-0 flex items-center")}
          initial={{ opacity: 0, y: motionOffset }}
          animate={{ opacity: 1, y: 0 }}
          exit={{ opacity: 0, y: -motionOffset }}
          transition={{ duration: shouldReduceMotion ? 0 : FLIP_DURATION_S, ease: "easeOut" }}
        >
          {renderSlide(activeAttribute)}
        </motion.div>
      </AnimatePresence>
    </div>
  );
};

ProductTagFlipper.propTypes = {
  productAttributes: PropTypes.arrayOf(PropTypes.object),
  isMobile: PropTypes.bool.isRequired,
  renderItem: PropTypes.func,
  isFromPDP: PropTypes.bool,
};

export default React.memo(ProductTagFlipper);
